import React from 'react'
import Image from 'next/future/image'
import F1Heading from '@shared/components/F1Components/F1Heading'
import BreadcrumbNav from '@shared/components/breadcrumbNav'
import Formula1Layout from '../layout'
import PropTypes from 'prop-types'

import styles from './style.module.scss'
import track from '@assets/images/f1/img/track.png'
import UpcomingEvent from '@shared/components/F1Components/widgets/upcomingEvent'
import { formatDate, handleApiError } from '@shared-submodule/utils'
import CustomLink from '@shared/components/customLink'
import queryGraphql from '@shared/components/queryGraphql'
import Layout from '@shared/components/layout'
import { GRAPHQL_URL } from '@shared-submodule/sm-constants'

const CircuitCard = ({ circuit, isFinished }) => {
  function getDate() {
    if (formatDate(circuit?.firstPracticeDate, 'MMM') === formatDate(circuit?.date, 'MMM')) {
      return `${formatDate(circuit?.firstPracticeDate, 'MMM DD')} - ${formatDate(circuit?.date, 'DD')}`
    }
    return `${formatDate(circuit?.firstPracticeDate, 'MMM DD')} - ${formatDate(circuit?.date, 'MMM DD')}`
  }
  return (
    <div className={`${styles.circuit} position-relative`}>
      {circuit?.oCircuit?.sArticleUrl && (
        <CustomLink href={circuit?.oCircuit?.sArticleUrl}>
          <a className="position-absolute top-0 start-0 w-100 h-100 opacity-0">{`Round ${circuit?.round}`}</a>
        </CustomLink>
      )}
      <div className={styles.round}>{`Round ${circuit?.round}`}</div>
      <div className="d-flex flex-column gap-2">
        <div className={styles.countryData}>
          <span className={styles.country}>
            <span className={styles.line}></span>
            {circuit?.country}
          </span>
          <Image src={circuit?.flag} alt={`${circuit?.country} flag`} width={44} height={30} />
        </div>
        <div className={styles.date}>
          <p className={styles.about}>{isFinished ? circuit?.raceName : circuit?.oCircuit?.sName}</p>
          <div className={styles.event}>
            <span>{getDate()}</span>
          </div>
        </div>
      </div>
      {isFinished && (
        <div className={styles.drivers}>
          {circuit?.topDrivers.map(img => (
            <Image
              src={img?.image}
              alt={`${img?.name} track layout`}
              key={img?.position}
              width={80}
              height={90}
              className={styles.driver}
            />
          ))}
        </div>
      )}
      {!isFinished && (
        <div className="d-flex justify-content-center">
          <Image
            src={circuit?.oCircuit?.sImgUrl || track}
            alt={`${circuit?.oCircuit?.oLocation?.sCountry} track layout`}
            width={165}
            height={124}
          />
        </div>
      )}
    </div>
  )
}

CircuitCard.propTypes = {
  circuit: PropTypes.array,
  isFinished: PropTypes.bool
}

const Circuits = ({ raceData, seoData }) => {
  const completedRaces = raceData?.data?.finishedRaces
  const upcomingData = raceData?.data?.scheduledRaces
  return (
    <Layout data={{ oSeo: seoData }}>
      <Formula1Layout>
        <div className={styles.circuitsLayout}>
          <div className="flex flex-column">
            <BreadcrumbNav />
            <F1Heading title="F1 Schedule 2025" />
            <p className={styles.about}>2025 FIA FORMULA ONE WORLD CHAMPIONSHIP™ RACE CALENDAR</p>
          </div>
          <div className={styles.circuits} style={{ marginBottom: '60px' }}>
            {(completedRaces ?? []).map(circuit => (
              <CircuitCard key={circuit?.nRound} circuit={circuit} isFinished />
            ))}
          </div>
          <UpcomingEvent upcoming={upcomingData[0]} />
          <div className={styles.circuits}>
            {(upcomingData ?? []).slice(1).map(circuit => (
              <CircuitCard key={circuit?.nRound} circuit={circuit} />
            ))}
          </div>
        </div>
      </Formula1Layout>
    </Layout>
  )
}

Circuits.propTypes = {
  seoData: PropTypes.object,
  raceData: PropTypes.object
}

export default Circuits

export async function getServerSideProps({ res, resolvedUrl }) {
  try {
    res.setHeader('Cache-Control', 'public, max-age=600')

    const BASE_URL = GRAPHQL_URL.replace(/\/graphql$/, '')

    const urlRaceResults = new URL(`${BASE_URL}/api/global-widget/f1/fetch/finished-race-results`)
    urlRaceResults.searchParams.append('nYear', '2025')

    const GET_SEO_DATA = (await import('@graphql/article/article.query')).GET_SEO_DATA
    const { data: seo } = await queryGraphql(GET_SEO_DATA, { input: { sSlug: 'formula1-racing/circuits' } })

    const racesResponse = await fetch(urlRaceResults, {
      method: 'GET',
      headers: {
        'x-api-key': process.env.REACT_APP_API_KEY,
        'Content-Type': 'application/json'
      }
    })
    const raceData = await racesResponse.json()

    return {
      props: {
        seoData: seo?.getSeoData || {},
        raceData: raceData || {}
      }
    }
  } catch (e) {
    console.error('Error fetching data:', e)
    res.setHeader('Cache-Control', 'no-cache')
    const status = handleApiError(e, resolvedUrl)
    return status
  }
}
